import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Stat, DisclaimerFooter } from "@/components/Bits";
import { useChallenge } from "@/context/ChallengeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fmtUSD, fmtDatePL } from "@/lib/calc";
import { Plug, RefreshCw, Link2Off, Loader2, ShieldCheck, Activity } from "lucide-react";
import { toast } from "sonner";

export default function BrokerMT5() {
  const { refresh } = useChallenge();
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ token: "", account_id: "", name: "THE MONEY CLUB MT5", login: "", server: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/mt5/status");
      setStatus(data);
    } catch (e) {
      /* ignore */
    }
  };
  useEffect(() => {
    loadStatus();
  }, []);

  // auto-sync every 30s when connected
  useEffect(() => {
    if (!status?.connected) return;
    const t = setInterval(() => doSync(true), 30000);
    return () => clearInterval(t);
  }, [status?.connected]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const connect = async () => {
    if (!form.token || (!form.account_id && (!form.login || !form.server || !form.password))) {
      toast.error("Podaj token MetaAPI oraz Account ID (lub login + serwer + hasło)");
      return;
    }
    setBusy(true);
    try {
      await api.post("/mt5/connect", form);
      toast.success("Konto MT5 podłączone", { description: "Synchronizuję dane..." });
      setForm((f) => ({ ...f, password: "" }));
      await loadStatus();
      await doSync(false);
    } catch (e) {
      toast.error("Błąd połączenia", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setBusy(false);
    }
  };

  const doSync = async (silent) => {
    setSyncing(true);
    try {
      const { data } = await api.post("/mt5/sync");
      if (!silent)
        toast.success("Zsynchronizowano", {
          description: `Saldo: ${fmtUSD(data.balance || 0)} · transakcji: ${data.deals_count} · tur wypełnionych: ${data.sessions_filled}`,
        });
      await loadStatus();
      await refresh(true);
    } catch (e) {
      if (!silent) toast.error("Synchronizacja nieudana", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setSyncing(false);
    }
  };

  const disconnect = async () => {
    await api.post("/mt5/disconnect");
    toast("Konto MT5 odłączone");
    await loadStatus();
  };

  const connected = status?.connected;

  return (
    <div>
      <PageHeader
        title="Broker · MT5"
        subtitle="Automatyczny odczyt konta MetaTrader 5 przez MetaAPI (tylko do odczytu)."
        right={
          connected && (
            <Button
              data-testid="mt5-sync-button"
              onClick={() => doSync(false)}
              disabled={syncing}
              className="font-display font-semibold bg-gradient-to-r from-violet-700 to-violet-500 border-0"
            >
              {syncing ? <Loader2 className="animate-spin mr-2" size={16} /> : <RefreshCw size={16} className="mr-2" />}
              SYNCHRONIZUJ
            </Button>
          )
        }
      />

      {connected ? (
        <div className="space-y-6">
          <div className="glow-card rounded-2xl p-6" data-testid="mt5-status-card">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" style={{ boxShadow: "0 0 10px #10b981" }} />
              <span className="font-display font-semibold text-emerald-400">POŁĄCZONO</span>
              <span className="ml-auto text-xs text-demon-muted font-mono">
                {status.last_sync ? "Ostatnia sync: " + new Date(status.last_sync).toLocaleString("pl-PL") : "Brak synchronizacji"}
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Stat label="SALDO" value={fmtUSD(status.balance || 0)} accent="violet" testid="mt5-balance" />
              <Stat label="EQUITY" value={fmtUSD(status.equity || 0)} accent="green" />
              <Stat label="LOGIN" value={status.login || "—"} />
              <Stat label="TRANSAKCJE" value={status.deals_imported || 0} />
            </div>
            <div className="mt-4 text-sm text-demon-muted font-mono">
              Serwer: <span className="text-white">{status.server}</span> · Nazwa: <span className="text-white">{status.name}</span>
            </div>
          </div>

          <div className="glow-card rounded-2xl p-6 flex items-start gap-4">
            <Activity className="text-violet-400 mt-1" size={22} />
            <div className="text-sm text-demon-muted">
              Panel odświeża dane co 30 sekund. Realne transakcje z MT5 są automatycznie mapowane na 3 tury dnia
              (rano / po południu / wieczorem) w oknie challenge'u 01.10 – 31.12.2026 i aktualizują kapitał.
            </div>
          </div>

          <Button
            data-testid="mt5-disconnect-button"
            onClick={disconnect}
            variant="outline"
            className="border-red-500/40 text-red-300 hover:bg-red-500/10 bg-transparent"
          >
            <Link2Off size={16} className="mr-2" /> Odłącz konto
          </Button>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="glow-card rounded-2xl p-6 lg:col-span-2">
            <div className="flex items-center gap-2 mb-5">
              <Plug className="text-violet-400" size={22} />
              <h2 className="font-display font-semibold text-lg">Podłącz konto MetaTrader 5</h2>
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-xs tracking-widest text-demon-muted">TOKEN METAAPI</Label>
                <Input
                  data-testid="mt5-token-input"
                  type="password"
                  value={form.token}
                  onChange={set("token")}
                  placeholder="Wklej token z app.metaapi.cloud/token"
                  className="mt-1.5 bg-black/50 border-violet-900/40 h-11 text-white font-mono"
                />
              </div>
              <div>
                <Label className="text-xs tracking-widest text-demon-muted">ACCOUNT ID (METAAPI)</Label>
                <Input
                  data-testid="mt5-accountid-input"
                  value={form.account_id}
                  onChange={set("account_id")}
                  placeholder="Wklej Account ID z panelu MetaAPI"
                  className="mt-1.5 bg-black/50 border-violet-900/40 h-11 text-white font-mono"
                />
                <p className="text-[11px] text-demon-muted/70 mt-1.5">
                  Zalecane: dodaj konto w panelu MetaAPI i wklej tu jego Account ID + API token.
                </p>
              </div>
              <div className="text-center">
                <span className="text-[10px] tracking-widest text-demon-muted">— ALBO PODAJ DANE KONTA —</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs tracking-widest text-demon-muted">LOGIN (NR KONTA)</Label>
                  <Input data-testid="mt5-login-input" value={form.login} onChange={set("login")} placeholder="12345678" className="mt-1.5 bg-black/50 border-violet-900/40 h-11 text-white font-mono" />
                </div>
                <div>
                  <Label className="text-xs tracking-widest text-demon-muted">SERWER</Label>
                  <Input data-testid="mt5-server-input" value={form.server} onChange={set("server")} placeholder="ICMarketsSC-Demo" className="mt-1.5 bg-black/50 border-violet-900/40 h-11 text-white font-mono" />
                </div>
              </div>
              <div>
                <Label className="text-xs tracking-widest text-demon-muted">HASŁO INWESTORSKIE (ODCZYT)</Label>
                <Input data-testid="mt5-password-input" type="password" value={form.password} onChange={set("password")} placeholder="Hasło tylko do odczytu" className="mt-1.5 bg-black/50 border-violet-900/40 h-11 text-white font-mono" />
              </div>
              <Button
                data-testid="mt5-connect-button"
                onClick={connect}
                disabled={busy}
                className="w-full h-12 font-display font-semibold bg-gradient-to-r from-violet-700 to-violet-500 border-0 shadow-[0_0_20px_rgba(168,85,247,0.35)]"
              >
                {busy ? <Loader2 className="animate-spin mr-2" size={18} /> : <Plug size={18} className="mr-2" />}
                POŁĄCZ I SYNCHRONIZUJ
              </Button>
            </div>
          </div>

          <div className="glow-card rounded-2xl p-6">
            <ShieldCheck className="text-emerald-400 mb-3" size={24} />
            <h3 className="font-display font-semibold mb-3">Bezpieczeństwo</h3>
            <ul className="text-sm text-demon-muted space-y-2 list-disc pl-4">
              <li>Używaj <span className="text-white">hasła inwestorskiego</span> — daje dostęp tylko do odczytu.</li>
              <li>Token i dane trzymane są po stronie serwera, nigdy w przeglądarce.</li>
              <li>Panel nie składa żadnych zleceń — wyłącznie czyta konto.</li>
              <li>Token wygenerujesz na app.metaapi.cloud/token.</li>
            </ul>
          </div>
        </div>
      )}

      <DisclaimerFooter />
    </div>
  );
}
